import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from '../components/Navbar';
import SEO from '../components/SEO';

// Import images
import profileImage from '../assets/images/profile.webp';
import heroBackground from '../assets/images/hero-bg.webp';

// Define translations
const aboutTranslations = {
  en: {
    name: "Barkın Sayın",
    role: "Entrepreneur, Investor, Visionary",
    intro1: "I'm Barkın Sayın – a builder, a believer, and an entrepreneur with a purpose. My journey began not in a corporate boardroom, but in moments of curiosity and defiance. I've always believed that great things happen when bold ideas meet relentless execution.",
    intro2: "Over the years, I've founded companies that didn't just succeed – they shifted mindsets. From investment technologies to sports, from media platforms to real estate, my mission has always been the same: Create real impact. Empower people. Think global.",
    quote: "\"Live your life by your own principles. Walk your path with courage – that is true success.\"",
    philosophyTitle: "My Philosophy",
    philosophy1: "I don't just start companies – I build ecosystems. I don't chase trends – I create my own path. Each venture I lead is rooted in purpose, driven by curiosity, and designed to challenge the ordinary.",
    philosophy2: "I believe true leadership isn't about control - it's about empowering people, amplifying potential, and turning ideas into momentum. My vision is to create companies that don't just operate - they inspire, they shift industries, and they leave something that matters.",
    philosophy3: "I'm not here for noise. I'm here to build. To shape a future that's smarter, faster, and more connected - and to bring others along for the journey.",
    valuesTitle: "Core Values",
    innovationTitle: "Innovation",
    innovationText: "I believe in challenging conventional thinking and pushing boundaries to create solutions that make a real difference.",
    globalImpactTitle: "Global Impact",
    globalImpactText: "My vision extends beyond borders. I build with a global mindset, aiming to create positive change worldwide.",
    purposeDrivenTitle: "Purpose-Driven",
    purposeDrivenText: "Every venture I create is built with clear purpose - solving real problems and creating meaningful value.",
    adaptabilityTitle: "Adaptability",
    adaptabilityText: "I embrace change and view challenges as opportunities to evolve, innovate, and grow stronger.",
    empowermentTitle: "Empowerment",
    empowermentText: "I believe in building people, not just companies. Success means elevating others and creating opportunities.",
    executionTitle: "Execution",
    executionText: "Ideas are valuable, but execution is everything. I focus on turning vision into reality through decisive action.",
    journeyTitle: "My Journey (2009 – 2025)",
    journeySubtitle: "It all started from scratch. And today, I lead projects that touch millions of users in the triangle of Germany, Turkey and Dubai.",
    journey2023: "2023 - 2025",
    journey2023Title: "Global Finance & Digital Consulting",
    journey2023Text: "This phase reflects a strategic focus on building globally relevant financial technology platforms. I launched Trade on TV, a smart TV application that democratizes financial data across asset classes—stocks, crypto, commodities, and global indices—by combining real-time data, AI analytics, and portfolio tools in an intuitive format. Concurrently, I expanded my advisory role through World Business AG, offering strategic consulting, legal structuring, and cross-border business support to entrepreneurs, executives, and high-profile individuals. In Dubai, I lead AllSoftware24, a software company specializing in smart TV and mobile solutions designed for mass adoption and global scalability. This period marks the fusion of finance, technology, and international business strategy.",
    journey2020: "2020 - 2023",
    journey2020Title: "Technology-Driven Business Models",
    journey2020Text: "Founded multiple companies operating in fintech, real estate technology, and digital media. These ventures focused on solving complex problems through user-oriented platforms, setting the stage for rapid scalability and future integration of AI-powered solutions.",
    journey2018: "2018 - 2020",
    journey2018Title: "Investment & Opportunity Mapping",
    journey2018Text: "I designed and executed cross-sector investment strategies with a strong focus on emerging markets and digital transformation. This phase strengthened my ability to identify scalable business models and act swiftly in dynamic environments.",
    journey2015: "2015 - 2018",
    journey2015Title: "First Independent Ventures",
    journey2015Text: "Launched my first independent ventures, building a foundation for sustainable growth. This period was characterized by hands-on learning, risk-taking, and a long-term entrepreneurial mindset focused on value creation and market adaptation.",
    journey2009: "2009 - 2015",
    journey2009Title: "Real Estate & Early Entrepreneurship",
    journey2009Text: "After completing my Business Administration degree, I took my first entrepreneurial step in 2009 by acquiring and renovating a duplex property in Germany. Through high-quality refurbishment and strategic marketing, I sold the property at a significant profit. Encouraged by favorable conditions in Germany and across Europe, I continued investing in undervalued real estate assets. By 2013, I expanded into the gastronomy sector, gaining valuable operational experience and entering the SME landscape with a results-driven mindset.",
    journey1: "Leading initiatives to expand business operations across international markets, focusing on sustainable growth and innovation.",
    journey2: "Founded multiple technology companies focused on fintech, real estate, and media, creating innovative solutions for modern challenges.",
    journey3: "Developed and implemented successful investment strategies across diverse sectors, focusing on emerging technologies and markets.",
    journey4: "Launched first successful entrepreneurial ventures, establishing the foundation for future business endeavors."
  },
  tr: {
    name: "Barkın Sayın",
    role: "Girişimci, Yatırımcı, Vizyoner",
    intro1: "Ben Barkın Sayın – bir inşacı, bir inanan ve amacı olan bir girişimciyim. Yolculuğum kurumsal bir toplantı salonunda değil, merak ve meydan okuma anlarında başladı. Her zaman cesur fikirler ile kararlı uygulama buluştuğunda büyük şeylerin gerçekleştiğine inandım.",
    intro2: "Yıllar içinde, sadece başarılı olmakla kalmayan, zihniyetleri değiştiren şirketler kurdum. Yatırım teknolojilerinden spora, medya platformlarından gayrimenkule kadar misyonum her zaman aynı oldu: Gerçek etki yaratmak. İnsanları güçlendirmek. Küresel düşünmek.",
    quote: "\"Hayatını kendi prensiplerine göre yaşa. Yolunu cesaretle yürü – gerçek başarı budur.\"",
    philosophyTitle: "Felsefem",
    philosophy1: "Sadece şirket kurmuyorum – ekosistemler inşa ediyorum. Trendlerin peşinden koşmuyorum – kendi yolumu oluşturuyorum. Liderlik ettiğim her girişim amaca dayalı, merak ile yönlendirilen ve sıradanı zorlamak için tasarlanmıştır.",
    philosophy2: "Gerçek liderliğin kontrol etmek değil - insanlara güç vermek, potansiyeli artırmak ve fikirleri ivmeye dönüştürmek olduğuna inanıyorum. Vizyonum, sadece faaliyet gösteren değil - ilham veren, endüstrileri değiştiren ve önemli bir şey bırakan şirketler oluşturmaktır.",
    philosophy3: "Ben gürültü için burada değilim. Ben inşa etmek için buradayım. Daha akıllı, daha hızlı ve daha bağlantılı bir gelecek şekillendirmek için - ve başkalarını da bu yolculuğa dahil etmek için.",
    valuesTitle: "Temel Değerler",
    innovationTitle: "Yenilik",
    innovationText: "Geleneksel düşünceye meydan okumaya ve gerçek bir fark yaratan çözümler oluşturmak için sınırları zorlamaya inanıyorum.",
    globalImpactTitle: "Küresel Etki",
    globalImpactText: "Vizyonum sınırların ötesine uzanıyor. Dünya çapında olumlu değişim yaratmayı hedefleyen küresel bir zihniyetle inşa ediyorum.",
    purposeDrivenTitle: "Amaç Odaklı",
    purposeDrivenText: "Yarattığım her girişim, net bir amaçla inşa edilmiştir - gerçek sorunları çözmek ve anlamlı değer yaratmak.",
    adaptabilityTitle: "Uyumluluk",
    adaptabilityText: "Değişimi kucaklıyor ve zorlukları evrim geçirmek, yenilik yapmak ve daha güçlü büyümek için fırsatlar olarak görüyorum.",
    empowermentTitle: "Güçlendirme",
    empowermentText: "Sadece şirketler değil, insanlar inşa etmeye inanıyorum. Başarı, başkalarını yükseltmek ve fırsatlar yaratmak demektir.",
    executionTitle: "Uygulama",
    executionText: "Fikirler değerlidir, ancak uygulama her şeydir. Kararlı eylemlerle vizyonu gerçeğe dönüştürmeye odaklanıyorum.",
    journeyTitle: "Yolculuğum (2009 – 2025)",
    journeySubtitle: "Yenilikçilik, girişimcilik ve küresel vizyon ile şekillenen stratejik bir yol.",
    journey2023: "2023 - 2025",
    journey2023Title: "Küresel Finans ve Dijital Danışmanlık",
    journey2023Text: "Bu dönem, küresel olarak alakalı finans teknolojisi platformları kurma konusunda stratejik bir odaklanmayı yansıtıyor. Trade on TV'yi piyasaya sürdüm; gerçek zamanlı verileri, yapay zeka analizlerini ve portföy araçlarını sezgisel bir formatta birleştirerek hisse senetleri, kripto, emtia ve küresel endeksler gibi varlık sınıflarında finansal verileri demokratikleştiren akıllı TV uygulaması. Aynı zamanda, World Business AG aracılığıyla danışmanlık rolümü genişleterek girişimcilere, yöneticilere ve tanınmış kişilere stratejik danışmanlık, yasal yapılandırma ve sınır ötesi iş desteği sundum. Dubai'de, kitlesel benimseme ve küresel ölçeklenebilirlik için tasarlanmış akıllı TV ve mobil çözümlerde uzmanlaşmış bir yazılım şirketi olan AllSoftware24'ü yönetiyorum. Bu dönem, finans, teknoloji ve uluslararası iş stratejisinin birleşimini işaret ediyor.",
    journey2020: "2020 - 2023",
    journey2020Title: "Teknoloji Odaklı İş Modelleri",
    journey2020Text: "Fintech, gayrimenkul teknolojisi ve dijital medya alanlarında faaliyet gösteren birden fazla şirket kurdum. Bu girişimler, kullanıcı odaklı platformlar aracılığıyla karmaşık sorunları çözmeye odaklanarak, hızlı ölçeklenebilirlik ve yapay zeka destekli çözümlerin gelecekteki entegrasyonu için zemin hazırladı.",
    journey2018: "2018 - 2020",
    journey2018Title: "Yatırım ve Fırsat Haritalama",
    journey2018Text: "Yükselen pazarlar ve dijital dönüşüme güçlü bir odaklanma ile sektörler arası yatırım stratejileri tasarladım ve uyguladım. Bu aşama, ölçeklenebilir iş modellerini belirleme ve dinamik ortamlarda hızla hareket etme yeteneğimi güçlendirdi.",
    journey2015: "2015 - 2018",
    journey2015Title: "İlk Bağımsız Girişimler",
    journey2015Text: "Sürdürülebilir büyüme için bir temel oluşturarak ilk bağımsız girişimlerimi başlattım. Bu dönem, değer yaratma ve pazar adaptasyonuna odaklanan uygulamalı öğrenme, risk alma ve uzun vadeli girişimcilik zihniyetiyle karakterize edildi.",
    journey2009: "2009 - 2015",
    journey2009Title: "Gayrimenkul ve Erken Girişimcilik",
    journey2009Text: "İşletme eğitimimi tamamladıktan sonra, 2009 yılında Almanya'da dubleks bir mülkü satın alıp yenileyerek ilk girişimcilik adımımı attım. Yüksek kaliteli yenileme ve stratejik pazarlama sayesinde mülkü önemli bir kârla sattım. Almanya ve Avrupa genelindeki uygun koşullardan cesaret alarak, düşük değerli gayrimenkul varlıklarına yatırım yapmaya devam ettim. 2013 yılına kadar, değerli operasyonel deneyim kazanarak ve sonuç odaklı bir zihniyetle KOBİ sektörüne girerek gastronomi sektörüne genişledim.",
    journey1: "Sürdürülebilir büyüme ve yenilik odaklı olarak iş operasyonlarını uluslararası pazarlara genişletme girişimlerine liderlik.",
    journey2: "Fintech, gayrimenkul ve medya odaklı, modern zorluklar için yenilikçi çözümler yaratan çoklu teknoloji şirketleri kurdum.",
    journey3: "Yükselen teknolojilere ve pazarlara odaklanarak, çeşitli sektörlerde başarılı yatırım stratejileri geliştirdim ve uyguladım.",
    journey4: "Gelecekteki iş girişimleri için temel oluşturan ilk başarılı girişimcilik projelerini başlattım."
  },
  fr: {
    name: "Barkın Sayın",
    role: "Entrepreneur, Investisseur, Visionnaire",
    intro1: "Je suis Barkın Sayın – un bâtisseur, un croyant et un entrepreneur avec un objectif. Mon parcours n'a pas commencé dans une salle de conseil d'entreprise, mais dans des moments de curiosité et de défi. J'ai toujours cru que de grandes choses se produisent lorsque des idées audacieuses rencontrent une exécution implacable.",
    intro2: "Au fil des années, j'ai fondé des entreprises qui n'ont pas seulement réussi – elles ont changé les mentalités. Des technologies d'investissement au sport, des plateformes médiatiques à l'immobilier, ma mission a toujours été la même : Créer un impact réel. Autonomiser les gens. Penser global.",
    quote: "\"Vivez votre vie selon vos propres principes. Marchez sur votre chemin avec courage – c'est le vrai succès.\"",
    philosophyTitle: "Ma Philosophie",
    philosophy1: "Je ne me contente pas de créer des entreprises – je construis des écosystèmes. Je ne cours pas après les tendances – je crée mon propre chemin. Chaque entreprise que je dirige est enracinée dans un objectif, motivée par la curiosité et conçue pour défier l'ordinaire.",
    philosophy2: "Je crois que le véritable leadership ne consiste pas à contrôler - il s'agit d'autonomiser les personnes, d'amplifier le potentiel et de transformer les idées en dynamique. Ma vision est de créer des entreprises qui ne se contentent pas de fonctionner - elles inspirent, elles transforment les industries et elles laissent quelque chose qui compte.",
    philosophy3: "Je ne suis pas là pour le bruit. Je suis ici pour construire. Pour façonner un avenir plus intelligent, plus rapide et plus connecté - et pour emmener d'autres personnes dans ce voyage.",
    valuesTitle: "Valeurs Fondamentales",
    innovationTitle: "Innovation",
    innovationText: "Je crois en la remise en question de la pensée conventionnelle et en repoussant les limites pour créer des solutions qui font une réelle différence.",
    globalImpactTitle: "Impact Global",
    globalImpactText: "Ma vision s'étend au-delà des frontières. Je construis avec une mentalité globale, visant à créer un changement positif dans le monde entier.",
    purposeDrivenTitle: "Axé sur l'Objectif",
    purposeDrivenText: "Chaque entreprise que je crée est construite avec un objectif clair - résoudre de vrais problèmes et créer une valeur significative.",
    adaptabilityTitle: "Adaptabilité",
    adaptabilityText: "J'embrasse le changement et vois les défis comme des opportunités d'évoluer, d'innover et de devenir plus fort.",
    empowermentTitle: "Autonomisation",
    empowermentText: "Je crois en la construction de personnes, pas seulement d'entreprises. Le succès signifie élever les autres et créer des opportunités.",
    executionTitle: "Exécution",
    executionText: "Les idées sont précieuses, mais l'exécution est tout. Je me concentre sur la transformation de la vision en réalité par une action décisive.",
    journeyTitle: "Mon Parcours (2009 – 2025)",
    journeySubtitle: "Un chemin stratégique façonné par l'innovation, l'entrepreneuriat et une vision globale.",
    journey2023: "2023 - 2025",
    journey2023Title: "Finance Mondiale et Conseil Numérique",
    journey2023Text: "Cette phase reflète une concentration stratégique sur la construction de plateformes de technologie financière mondiales pertinentes. J'ai lancé Trade on TV, une application de télévision intelligente qui démocratise les données financières à travers les classes d'actifs - actions, crypto, matières premières et indices mondiaux - en combinant des données en temps réel, des analyses d'IA et des outils de portefeuille dans un format intuitif. Parallèlement, j'ai élargi mon rôle de conseil par le biais de World Business AG, offrant des conseils stratégiques, une structuration juridique et un soutien commercial transfrontalier aux entrepreneurs, dirigeants et personnes de haut profil. À Dubaï, je dirige AllSoftware24, une société de logiciels spécialisée dans les solutions de télévision intelligente et mobile conçues pour une adoption massive et une évolutivité mondiale. Cette période marque la fusion de la finance, de la technologie et de la stratégie commerciale internationale.",
    journey2020: "2020 - 2023",
    journey2020Title: "Modèles d'Affaires Axés sur la Technologie",
    journey2020Text: "J'ai fondé plusieurs entreprises opérant dans la fintech, la technologie immobilière et les médias numériques. Ces entreprises se sont concentrées sur la résolution de problèmes complexes grâce à des plateformes orientées utilisateur, préparant le terrain pour une scalabilité rapide et l'intégration future de solutions alimentées par l'IA.",
    journey2018: "2018 - 2020",
    journey2018Title: "Investissement et Cartographie des Opportunités",
    journey2018Text: "J'ai conçu et exécuté des stratégies d'investissement intersectorielles avec un fort accent sur les marchés émergents et la transformation numérique. Cette phase a renforcé ma capacité à identifier des modèles d'affaires évolutifs et à agir rapidement dans des environnements dynamiques.",
    journey2015: "2015 - 2018",
    journey2015Title: "Premières Ventures Indépendantes",
    journey2015Text: "J'ai lancé mes premières entreprises indépendantes, construisant une base pour une croissance durable. Cette période a été caractérisée par l'apprentissage pratique, la prise de risques et une mentalité entrepreneuriale à long terme axée sur la création de valeur et l'adaptation au marché.",
    journey2009: "2009 - 2015",
    journey2009Title: "Immobilier et Entrepreneuriat Précoce",
    journey2009Text: "Après avoir terminé mes études en administration des affaires, j'ai fait mon premier pas entrepreneurial en 2009 en acquérant et en rénovant une propriété duplex en Allemagne. Grâce à une rénovation de haute qualité et à un marketing stratégique, j'ai vendu la propriété avec un profit significatif. Encouragé par des conditions favorables en Allemagne et en Europe, j'ai continué à investir dans des actifs immobiliers sous-évalués. D'ici 2013, je me suis étendu au secteur de la gastronomie, acquérant une précieuse expérience opérationnelle et entrant dans le paysage des PME avec une mentalité axée sur les résultats.",
    journey1: "Direction d'initiatives visant à étendre les opérations commerciales sur les marchés internationaux, en mettant l'accent sur la croissance durable et l'innovation.",
    journey2: "Fondation de plusieurs entreprises technologiques axées sur la fintech, l'immobilier et les médias, créant des solutions innovantes pour les défis modernes.",
    journey3: "Développement et mise en œuvre de stratégies d'investissement réussies dans divers secteurs, en se concentrant sur les technologies et les marchés émergents.",
    journey4: "Lancement des premières entreprises entrepreneuriales réussies, établissant les bases des futures entreprises."
  },
  es: {
    name: "Barkın Sayın",
    role: "Emprendedor, Inversor, Visionario",
    intro1: "Soy Barkın Sayın – un constructor, un creyente y un emprendedor con propósito. Mi viaje no comenzó en una sala de juntas corporativa, sino en momentos de curiosidad y desafío. Siempre he creído que las grandes cosas suceden cuando las ideas audaces se encuentran con la ejecución implacable.",
    intro2: "A lo largo de los años, he fundado empresas que no solo tuvieron éxito, sino que cambiaron mentalidades. Desde tecnologías de inversión hasta deportes, desde plataformas de medios hasta bienes raíces, mi misión siempre ha sido la misma: Crear un impacto real. Empoderar a las personas. Pensar global.",
    quote: "\"Vive tu vida según tus propios principios. Camina tu sendero con coraje – eso es el verdadero éxito.\"",
    philosophyTitle: "Mi Filosofía",
    philosophy1: "No solo inicio empresas – construyo ecosistemas. No persigo tendencias – creo mi propio camino. Cada empresa que lidero está arraigada en un propósito, impulsada por la curiosidad y diseñada para desafiar lo ordinario.",
    philosophy2: "Creo que el verdadero liderazgo no se trata de control - se trata de empoderar a las personas, amplificar el potencial y convertir las ideas en impulso. Mi visión es crear empresas que no solo operen - inspiran, cambian industrias y dejan algo que importa.",
    philosophy3: "No estoy aquí para hacer ruido. Estoy aquí para construir. Para dar forma a un futuro más inteligente, más rápido y más conectado - y para llevar a otros en el viaje.",
    valuesTitle: "Valores Fundamentales",
    innovationTitle: "Innovación",
    innovationText: "Creo en desafiar el pensamiento convencional y empujar los límites para crear soluciones que marquen una diferencia real.",
    globalImpactTitle: "Impacto Global",
    globalImpactText: "Mi visión se extiende más allá de las fronteras. Construyo con una mentalidad global, con el objetivo de crear un cambio positivo en todo el mundo.",
    purposeDrivenTitle: "Orientado al Propósito",
    purposeDrivenText: "Cada empresa que creo se construye con un propósito claro - resolver problemas reales y crear valor significativo.",
    adaptabilityTitle: "Adaptabilidad",
    adaptabilityText: "Abrazo el cambio y veo los desafíos como oportunidades para evolucionar, innovar y fortalecerme.",
    empowermentTitle: "Empoderamiento",
    empowermentText: "Creo en construir personas, no solo empresas. El éxito significa elevar a otros y crear oportunidades.",
    executionTitle: "Ejecución",
    executionText: "Las ideas son valiosas, pero la ejecución lo es todo. Me centro en convertir la visión en realidad a través de una acción decisiva.",
    journeyTitle: "Mi Trayectoria (2009 – 2025)",
    journeySubtitle: "Un camino estratégico moldeado por la innovación, el emprendimiento y la visión global.",
    journey2023: "2023 - 2025",
    journey2023Title: "Finanzas Globales y Consultoría Digital",
    journey2023Text: "Esta fase refleja un enfoque estratégico en la construcción de plataformas de tecnología financiera relevantes a nivel mundial. Lancé Trade on TV, una aplicación de televisión inteligente que democratiza los datos financieros a través de diversas clases de activos (acciones, criptomonedas, materias primas e índices globales) combinando datos en tiempo real, análisis de IA y herramientas de cartera en un formato intuitivo. Paralelamente, amplié mi rol de asesoría a través de World Business AG, ofreciendo consultoría estratégica, estructuración legal y apoyo empresarial transfronterizo a emprendedores, ejecutivos y personas de alto perfil. En Dubái, lidero AllSoftware24, una empresa de software especializada en soluciones para televisión inteligente y móviles diseñadas para la adopción masiva y la escalabilidad global. Este período marca la fusión de las finanzas, la tecnología y la estrategia empresarial internacional.",
    journey2020: "2020 - 2023",
    journey2020Title: "Modelos de Negocio Impulsados por la Tecnología",
    journey2020Text: "Fundé múltiples empresas operando en fintech, tecnología inmobiliaria y medios digitales. Estos emprendimientos se enfocaron en resolver problemas complejos a través de plataformas orientadas al usuario, preparando el terreno para una rápida escalabilidad y la futura integración de soluciones impulsadas por IA.",
    journey2018: "2018 - 2020",
    journey2018Title: "Inversión y Mapeo de Oportunidades",
    journey2018Text: "Diseñé y ejecuté estrategias de inversión intersectoriales con un fuerte enfoque en mercados emergentes y transformación digital. Esta fase fortaleció mi capacidad para identificar modelos de negocio escalables y actuar rápidamente en entornos dinámicos.",
    journey2015: "2015 - 2018",
    journey2015Title: "Primeras Empresas Independientes",
    journey2015Text: "Lancé mis primeras empresas independientes, construyendo una base para el crecimiento sostenible. Este período se caracterizó por el aprendizaje práctico, la toma de riesgos y una mentalidad empresarial a largo plazo centrada en la creación de valor y la adaptación al mercado.",
    journey2009: "2009 - 2015",
    journey2009Title: "Bienes Raíces y Emprendimiento Temprano",
    journey2009Text: "Después de completar mi licenciatura en Administración de Empresas, di mi primer paso empresarial en 2009 adquiriendo y renovando una propiedad dúplex en Alemania. A través de una renovación de alta calidad y marketing estratégico, vendí la propiedad con un beneficio significativo. Animado por las condiciones favorables en Alemania y en toda Europa, continué invirtiendo en activos inmobiliarios infravalorados. Para 2013, me expandí al sector de la gastronomía, adquiriendo valiosa experiencia operativa y entrando en el panorama de las PYME con una mentalidad orientada a resultados.",
    journey1: "Liderando iniciativas para expandir operaciones comerciales en mercados internacionales, enfocándose en crecimiento sostenible e innovación.",
    journey2: "Fundé múltiples compañías tecnológicas enfocadas en fintech, bienes raíces y medios, creando soluciones innovadoras para desafíos modernos.",
    journey3: "Desarrollé e implementé estrategias de inversión exitosas en diversos sectores, enfocándome en tecnologías y mercados emergentes.",
    journey4: "Lancé mis primeras empresas emprendedoras exitosas, estableciendo la base para futuros emprendimientos comerciales."
  },
  ar: {
    name: "باركين سايين",
    role: "رائد أعمال، مستثمر، صاحب رؤية",
    intro1: "أنا باركين سايين - بنّاء، مؤمن، ورائد أعمال ذو هدف. لم تبدأ رحلتي في قاعة اجتماعات مؤسسية، بل في لحظات من الفضول والتحدي. لطالما آمنت أن الأشياء العظيمة تحدث عندما تلتقي الأفكار الجريئة مع التنفيذ الدؤوب.",
    intro2: "على مر السنين، أسست شركات لم تنجح فحسب - بل غيرت العقليات. من تقنيات الاستثمار إلى الرياضة، ومن منصات الإعلام إلى العقارات، كانت مهمتي دائمًا هي نفسها: خلق تأثير حقيقي. تمكين الناس. التفكير عالميًا.",
    quote: "\"عش حياتك وفقًا لمبادئك الخاصة. امشِ في طريقك بشجاعة - هذا هو النجاح الحقيقي.\"",
    philosophyTitle: "فلسفتي",
    philosophy1: "أنا لا أبدأ الشركات فقط - أنا أبني أنظمة بيئية متكاملة. لا أتبع الاتجاهات - أخلق مساري الخاص. كل مشروع أقوده متجذر في الهدف، مدفوع بالفضول، ومصمم لتحدي المألوف.",
    philosophy2: "أؤمن أن القيادة الحقيقية ليست متعلقة بالسيطرة - بل بتمكين الناس، وتضخيم الإمكانات، وتحويل الأفكار إلى زخم. رؤيتي هي إنشاء شركات لا تعمل فقط - بل تلهم، وتغير الصناعات، وتترك شيئًا ذا قيمة.",
    philosophy3: "أنا لست هنا للضجيج. أنا هنا للبناء. لتشكيل مستقبل أكثر ذكاءً وسرعة واتصالاً - ولإحضار الآخرين معي في هذه الرحلة.",
    valuesTitle: "القيم الأساسية",
    innovationTitle: "الابتكار",
    innovationText: "أؤمن بتحدي التفكير التقليدي ودفع الحدود لخلق حلول تحدث فرقًا حقيقيًا.",
    globalImpactTitle: "التأثير العالمي",
    globalImpactText: "رؤيتي تمتد إلى ما وراء الحدود. أبني بعقلية عالمية، وأهدف إلى خلق تغيير إيجابي في جميع أنحاء العالم.",
    purposeDrivenTitle: "مدفوع بالهدف",
    purposeDrivenText: "كل مشروع أنشئه مبني بهدف واضح - حل مشاكل حقيقية وخلق قيمة ذات معنى.",
    adaptabilityTitle: "التكيف",
    adaptabilityText: "أحتضن التغيير وأرى التحديات كفرص للتطور والابتكار والنمو بشكل أقوى.",
    empowermentTitle: "التمكين",
    empowermentText: "أؤمن ببناء الأشخاص، وليس الشركات فقط. النجاح يعني رفع الآخرين وخلق الفرص.",
    executionTitle: "التنفيذ",
    executionText: "الأفكار قيمة، لكن التنفيذ هو كل شيء. أركز على تحويل الرؤية إلى واقع من خلال العمل الحاسم.",
    journeyTitle: "رحلتي (2009 – 2025)",
    journeySubtitle: "مسار استراتيجي تشكل من خلال الابتكار وريادة الأعمال والرؤية العالمية.",
    journey2023: "2023 - 2025",
    journey2023Title: "التمويل العالمي والاستشارات الرقمية",
    journey2023Text: "تعكس هذه المرحلة تركيزاً استراتيجياً على بناء منصات تكنولوجيا مالية ذات صلة عالمياً. أطلقت Trade on TV، وهو تطبيق تلفزيون ذكي يقوم بتوفير البيانات المالية عبر فئات الأصول المختلفة—الأسهم والعملات المشفرة والسلع والمؤشرات العالمية—من خلال الجمع بين البيانات في الوقت الفعلي وتحليلات الذكاء الاصطناعي وأدوات المحفظة في شكل بديهي. وبالتوازي مع ذلك، وسعت دوري الاستشاري من خلال World Business AG، مقدماً استشارات استراتيجية وهيكلة قانونية ودعم تجاري عبر الحدود لرواد الأعمال والمديرين التنفيذيين والشخصيات البارزة. في دبي، أقود AllSoftware24، وهي شركة برمجيات متخصصة في حلول التلفزيون الذكي والهاتف المحمول المصممة للاعتماد الجماعي والقابلية للتوسع العالمية. تميز هذه الفترة اندماج التمويل والتكنولوجيا واستراتيجية الأعمال الدولية.",
    journey2020: "2020 - 2023",
    journey2020Title: "نماذج الأعمال المدفوعة بالتكنولوجيا",
    journey2020Text: "أسست عدة شركات تعمل في مجال التكنولوجيا المالية وتكنولوجيا العقارات والإعلام الرقمي. ركزت هذه المشاريع على حل المشاكل المعقدة من خلال منصات موجهة للمستخدم، مما مهد الطريق للتوسع السريع والتكامل المستقبلي للحلول المدعومة بالذكاء الاصطناعي.",
    journey2018: "2018 - 2020",
    journey2018Title: "الاستثمار ورسم خرائط الفرص",
    journey2018Text: "صممت ونفذت استراتيجيات استثمارية متعددة القطاعات مع تركيز قوي على الأسواق الناشئة والتحول الرقمي. عززت هذه المرحلة قدرتي على تحديد نماذج الأعمال القابلة للتوسع والعمل بسرعة في البيئات الديناميكية.",
    journey2015: "2015 - 2018",
    journey2015Title: "المشاريع المستقلة الأولى",
    journey2015Text: "أطلقت مشاريعي المستقلة الأولى، بناء أساس للنمو المستدام. تميزت هذه الفترة بالتعلم العملي، وتحمل المخاطر، وعقلية ريادة الأعمال طويلة الأجل التي تركز على خلق القيمة والتكيف مع السوق.",
    journey2009: "2009 - 2015",
    journey2009Title: "العقارات وريادة الأعمال المبكرة",
    journey2009Text: "بعد إكمال درجتي في إدارة الأعمال، اتخذت خطوتي الأولى في ريادة الأعمال في عام 2009 من خلال الاستحواذ على عقار مزدوج في ألمانيا وتجديده. من خلال التجديد عالي الجودة والتسويق الاستراتيجي، بعت العقار بربح كبير. مشجعاً بالظروف المواتية في ألمانيا وعبر أوروبا، واصلت الاستثمار في الأصول العقارية منخفضة القيمة. بحلول عام 2013، توسعت في قطاع المطاعم، مكتسباً خبرة تشغيلية قيمة ودخلت مشهد الشركات الصغيرة والمتوسطة بعقلية مركزة على النتائج.",
    journey1: "قيادة المبادرات لتوسيع العمليات التجارية عبر الأسواق الدولية، مع التركيز على النمو المستدام والابتكار.",
    journey2: "أسست العديد من شركات التكنولوجيا التي تركز على التكنولوجيا المالية والعقارات والإعلام، مما يخلق حلولًا مبتكرة للتحديات الحديثة.",
    journey3: "تطوير وتنفيذ استراتيجيات استثمارية ناجحة عبر قطاعات متنوعة، مع التركيز على التقنيات والأسواق الناشئة.",
    journey4: "أطلقت أول المشاريع الريادية الناجحة، مما أرسى الأساس للمشاريع التجارية المستقبلية."
  }
};

// Animations
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const shine = keyframes`
  0% { background-position: -100% 0; }
  100% { background-position: 200% 0; }
`;

const AboutSection = styled.section`
  padding: 120px 0 80px;
  position: relative;
  background: linear-gradient(to bottom, #ffffff, #f5f5f5);
  color: #333333;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 90% 10%, rgba(0, 0, 0, 0.03), rgba(0,0,0,0) 50%),
      linear-gradient(to bottom right, rgba(0, 0, 0, 0.03), rgba(0,0,0,0) 70%);
    pointer-events: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23e0e0e0' fill-opacity='0.15' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E");
    opacity: 0.15;
    pointer-events: none;
  }
`;

const AboutContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 30px;
  position: relative;
  z-index: 5;
`;

const AboutHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  text-align: left;
  margin-bottom: 100px;
  gap: 60px;
  
  @media (max-width: 992px) {
    flex-direction: column;
    text-align: center;
    align-items: center;
  }
`;

const AboutImageWrapper = styled(motion.div)`
  position: relative;
  order: 2;
  width: 380px;
  margin-top: 30px;
  
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border: 3px solid rgba(198, 172, 175, 0.3);
    z-index: 2;
    pointer-events: none;
    border-radius: 10px;
  }
  
  @media (max-width: 992px) {
    order: 1;
    margin-bottom: 40px;
    margin-top: 0;
  }
  
  @media (max-width: 768px) {
    width: 300px;
  }
`;

const AboutImage = styled(motion.div)<{ backgroundImage: string }>`
  width: 100%;
  height: 450px;
  background-image: url(${props => props.backgroundImage});
  background-size: cover;
  background-position: center;
  border-radius: 10px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,0.1));
    z-index: 1;
    pointer-events: none;
    border-radius: 10px;
  }
  
  @media (max-width: 768px) {
    height: 380px;
  }
`;

const Decorator = styled(motion.div)`
  position: absolute;
  border: 1px solid rgba(198, 172, 175, 0.2);
  border-radius: 50%;
  z-index: 1;
  
  &.top-right {
    top: -100px;
    right: -100px;
    width: 400px;
    height: 400px;
  }
  
  &.bottom-left {
    bottom: -150px;
    left: -150px;
    width: 500px;
    height: 500px;
    border-width: 2px;
  }
`;

const AboutContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 600px;
  order: 1;
  
  @media (max-width: 992px) {
    order: 2;
    align-items: center;
  }
`;

const AboutTitle = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 300;
  margin-bottom: 1.5rem;
  position: relative;
  color: #333333;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  
  &:after {
    content: '';
    position: absolute;
    width: 80px;
    height: 2px;
    background-color: #e29578;
    bottom: -15px;
    left: 0;
    
    @media (max-width: 992px) {
      left: 50%;
      transform: translateX(-50%);
    }
  }
  
  @media (max-width: 768px) {
    font-size: 2.8rem;
  }
`;

const AboutSubtitle = styled(motion.p)`
  font-size: 1.2rem;
  margin-bottom: 2rem;
  line-height: 1.6;
  color: #e29578;
  letter-spacing: 0.05em;
  font-weight: 300;
  text-transform: uppercase;
`;

const AboutText = styled(motion.p)`
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 1.8rem;
  color: #333333;
  text-align: left;
  
  @media (max-width: 992px) {
    text-align: center;
  }
`;

const AboutQuote = styled(motion.div)`
  padding: 30px 0;
  margin: 30px 0;
  font-style: italic;
  font-size: 1.3rem;
  line-height: 1.6;
  position: relative;
  color: #333333;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  
  &::before {
    content: '"';
    position: absolute;
    top: 0;
    left: 0;
    font-size: 4rem;
    font-family: Georgia, serif;
    line-height: 1;
    color: rgba(0, 0, 0, 0.1);
  }
`;

const SectionHeading = styled(motion.h2)<{ themeMode?: 'light' | 'dark' }>`
  font-size: 2.5rem;
  font-weight: 300;
  margin-bottom: 2.5rem;
  text-align: center;
  color: ${props => props.themeMode === 'light' ? '#463f3a' : '#ffffff'};
  letter-spacing: 0.05em;
  text-transform: uppercase;
  position: relative;
  
  &::after {
    content: '';
    display: block;
    width: 60px;
    height: 1px;
    background: #e29578;
    margin: 15px auto 0;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Highlight = styled.span`
  color: #e29578;
  font-weight: 400;
`;

const ValuesSection = styled.section`
  padding: 100px 0;
  background: linear-gradient(135deg, #2d2d2d, #1a1a1a);
  position: relative;
  overflow: hidden;
  color: #f8f8f8;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23e29578' fill-opacity='0.12' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E");
    opacity: 0.15;
    pointer-events: none;
  }
`;

const ValuesContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 30px;
  position: relative;
  z-index: 5;
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin-top: 60px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const ValueCard = styled(motion.div)`
  background: rgba(34, 34, 34, 0.8);
  padding: 35px 30px;
  border-radius: 5px;
  border: 1px solid rgba(226, 149, 120, 0.3);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 15px rgba(226, 149, 120, 0.2);
    border-color: rgba(226, 149, 120, 0.5);
  }
`;

const ValueTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 500;
  margin-bottom: 20px;
  color: #ffffff;
  letter-spacing: 0.05em;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    width: 30px;
    height: 1px;
    background-color: rgba(226, 149, 120, 0.8);
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
  }
`;

const ValueText = styled.p`
  font-size: 1.05rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.8);
  max-width: 280px;
`;

const JourneySection = styled.section`
  padding: 100px 0;
  background: linear-gradient(to bottom, #ffffff, #f0f0f0);
  position: relative;
  overflow: hidden;
  color: #111;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%23d4af37' fill-opacity='0.05'%3E%3Cpath d='M0 38.59l2.83-2.83 1.41 1.41L1.41 40H0v-1.41zM0 20l2.83-2.83 1.41 1.41L1.41 21.41 0 20zm0 18.59l2.83-2.83 1.41 1.41L1.41 40H0v-1.41zM0 0l2.83 2.83 1.41-1.41L1.41 0H0v1.41zM0 17.17l2.83 2.83 1.41-1.41L1.41 15.76 0 17.17zM0 0l2.83 2.83 1.41-1.41L1.41 0H0v1.41zM21.41 40l2.83-2.83 1.41 1.41L22.83 40H21.41zM2.83 21.41l2.83-2.83 1.41 1.41L4.24 22.83 2.83 21.41zM2.83 0l2.83 2.83 1.41-1.41L4.24 0H2.83v1.41zM40 38.59l-2.83-2.83 1.41-1.41L40 37.17v1.41zm0-20L37.17 21.41l-1.41-1.41L38.59 17.17 40 18.59zm0 18.59l-2.83-2.83 1.41-1.41L40 37.17v1.41zm0-38.59L37.17 2.83l-1.41-1.41L38.59 0H40v1.41zm0 17.17L37.17 20l-1.41-1.41L38.59 15.76 40 17.17zm0-18.59L37.17 1.41l-1.41-1.41L38.59 0H40v1.41zM18.59 40l2.83-2.83 1.41 1.41L20 40h-1.41zM20 18.59l2.83-2.83 1.41 1.41L21.41 20 20 18.59zM18.59 0l2.83 2.83 1.41-1.41L20 0h-1.41zM0 40h1.41l2.83-2.83 1.41 1.41L2.83 40H0V38.59zm0-20h1.41l2.83-2.83 1.41 1.41L2.83 20H0V18.59zm0 20h1.41l2.83-2.83 1.41 1.41L2.83 40H0v-1.41zM17.17 40l2.83-2.83 1.41 1.41L18.59 40h-1.41zM0 20h1.41l2.83-2.83 1.41 1.41L2.83 20H0v-1.41zm0 18.59h1.41l2.83-2.83 1.41 1.41L2.83 40H0v-1.41zm20 0l2.83-2.83 1.41 1.41L21.41 40H20v-1.41zm0-18.59l2.83-2.83 1.41 1.41L21.41 20H20v-1.41zm0 0l2.83-2.83 1.41 1.41L21.41 20H20v-1.41zm0 18.59l2.83-2.83 1.41 1.41L21.41 40H20v-1.41zm0-18.59l2.83-2.83 1.41 1.41L21.41 20H20v-1.41zm0 0l2.83-2.83 1.41 1.41L21.41 20H20v-1.41zM12.59 40l2.83-2.83 1.41 1.41L14 40H12.59zM12.59 0l2.83 2.83 1.41-1.41L14 0H12.59zM0 40h1.41l2.83-2.83 1.41 1.41L2.83 40H0v-1.41zm0 0h1.41l2.83-2.83 1.41 1.41L2.83 40H0v-1.41z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    opacity: 0.1;
    pointer-events: none;
  }
`;

const JourneyContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 30px;
  position: relative;
  z-index: 5;
`;

const Timeline = styled.div`
  position: relative;
  margin-top: 60px;
  
  &:before {
    content: '';
    position: absolute;
    width: 2px;
    background-color: rgba(212, 175, 55, 0.3);
    top: 0;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    
    @media (max-width: 768px) {
      left: 30px;
    }
  }
`;

const TimelineItem = styled(motion.div)`
  padding: 10px 40px;
  position: relative;
  width: 50%;
  box-sizing: border-box;
  
  &:nth-child(odd) {
    left: 0;
  }
  
  &:nth-child(even) {
    left: 50%;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    padding-left: 70px;
    padding-right: 25px;
    left: 0 !important;
  }
`;

const TimelineContent = styled.div`
  padding: 35px 30px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 0;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
  position: relative;
  border: 1px solid rgba(212, 175, 55, 0.25);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  text-align: left;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.12), 0 0 15px rgba(212, 175, 55, 0.15);
    border-color: rgba(212, 175, 55, 0.4);
  }
  
  p {
    font-size: 1.05rem;
    line-height: 1.7;
    color: #444;
  }
`;

const TimelineDot = styled.div`
  position: absolute;
  width: 20px;
  height: 20px;
  background-color: #D4AF37;
  border-radius: 50%;
  top: 30px;
  right: -10px;
  z-index: 2;
  box-shadow: 0 0 10px rgba(212, 175, 55, 0.4);
  
  ${TimelineItem}:nth-child(even) & {
    left: -10px;
  }
  
  @media (max-width: 768px) {
    left: 21px !important;
    right: auto;
  }
`;

const TimelineDate = styled.div`
  font-weight: 700;
  color: #D4AF37;
  margin-bottom: 10px;
  letter-spacing: 0.05em;
`;

const TimelineTitle = styled.h3`
  margin-bottom: 15px;
  font-size: 1.3rem;
  color: #222;
  font-weight: 500;
`;

// Animation variants
const fadeIn = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { type: "tween", duration: 0.8 }
};

const staggerContainer = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3
    }
  }
};

const StyledPhilosophyContainer = styled(motion.div)`
  width: 100%;
  padding: 120px 16px 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 100px 16px 60px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 80px 16px 40px;
  }
`;

// Invisible anchor for better scroll positioning
const Anchor = styled.div`
  position: absolute;
  top: -70px; // Reduced from -200px to make section headers more visible
  visibility: hidden;
  display: block;
  width: 1px;
  height: 1px;
`;

const JourneySubtitle = styled(motion.p)`
  font-size: 1.2rem;
  text-align: center;
  max-width: 800px;
  margin: 0 auto 50px;
  color: #555;
  line-height: 1.6;
  font-weight: 300;
  letter-spacing: 0.02em;
  position: relative;
  
  &::after {
    content: '';
    display: block;
    width: 40px;
    height: 1px;
    background: #333;
    margin: 20px auto 0;
    opacity: 0.4;
  }
`;

const TimelineText = styled.p`
  margin-top: 15px;
  line-height: 1.6;
  color: #555;
`;

const About: React.FC = () => {
  const { language, t } = useLanguage();
  
  // Add SEO metadata for About page
  const aboutSeoData = {
    en: {
      title: 'About Barkin Sayin - Fintech Entrepreneur & Technology Leader',
      description: 'Learn about Barkin Sayin\'s journey, philosophy, and core values. A fintech entrepreneur and investor leading innovation in smart TV technologies and digital transformation.',
      keywords: [
        'Barkin Sayin about',
        'Barkin Sayin biography',
        'fintech entrepreneur background',
        'Trade on TV founder',
        'technology leader philosophy',
        'German Turkish entrepreneur'
      ]
    },
    tr: {
      title: 'Barkın Sayın Hakkında - Fintech Girişimcisi ve Teknoloji Lideri',
      description: 'Barkın Sayın\'ın yolculuğu, felsefesi ve temel değerlerini keşfedin. Akıllı TV teknolojileri ve dijital dönüşümde yenilikçiliği yönlendiren fintech girişimcisi ve yatırımcı.',
      keywords: [
        'Barkın Sayın hakkında',
        'Barkın Sayın biyografi',
        'fintech girişimcisi geçmişi',
        'Trade on TV kurucusu',
        'teknoloji lideri felsefesi',
        'Alman Türk girişimci'
      ]
    }
  };
  
  const seoTitle = aboutSeoData[language as keyof typeof aboutSeoData]?.title || aboutSeoData.en.title;
  const seoDescription = aboutSeoData[language as keyof typeof aboutSeoData]?.description || aboutSeoData.en.description;
  const seoKeywords = aboutSeoData[language as keyof typeof aboutSeoData]?.keywords || aboutSeoData.en.keywords;

  // Add effect to handle direct hash navigation
  useEffect(() => {
    // Check if there's a hash in the URL
    if (window.location.hash) {
      // Get the hash without the # character
      const id = window.location.hash.substring(1);
      
      // Wait for the page to fully render
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          // Using a smaller offset for philosophy and standard offsets for others
          const offsets: Record<string, number> = {
            'philosophy': 40,     // Reduced offset for philosophy to scroll less
            'coreValues': 80,     // Keep the same offset
            'journey': 80         // Keep the same offset
          };
          
          // Get the appropriate offset for the section or use default
          const headerOffset = offsets[id] || 80;
          
          // Calculate position with appropriate offset
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          
          // Smooth scroll to element with offset
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 500);
    }
  }, []);

  // Get text based on the current language
  const getText = (key: keyof typeof aboutTranslations.en): string => {
    if (language === 'en' || language === 'tr' || language === 'fr' || language === 'es' || language === 'ar') {
      return aboutTranslations[language as keyof typeof aboutTranslations][key];
    }
    return aboutTranslations.en[key];
  };

  return (
    <motion.div>
      <SEO 
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
      />
      
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        <AboutSection>
          <Decorator 
            className="top-right" 
            initial={{ opacity: 0, scale: 0.8 }} 
            animate={{ opacity: 0.2, scale: 1 }}
            transition={{ duration: 1.5 }}
          />
          <Decorator 
            className="bottom-left" 
            initial={{ opacity: 0, scale: 0.8 }} 
            animate={{ opacity: 0.2, scale: 1 }}
            transition={{ duration: 1.5, delay: 0.3 }}
          />
        
          <AboutContainer>
            <AboutHeader>
              <AboutImageWrapper
                initial={{ opacity: 0, y: -30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <AboutImage
                  backgroundImage={profileImage}
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                />
              </AboutImageWrapper>
              
              <AboutContent>
                <AboutTitle
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  {getText('name')}
                </AboutTitle>
                
                <AboutSubtitle
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                >
                  {getText('role')}
                </AboutSubtitle>
                
                <AboutText
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  {getText('intro1')}
                </AboutText>
                
                <AboutText
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                >
                  {getText('intro2')}
                </AboutText>
                
                <AboutQuote
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  {getText('quote')}
                </AboutQuote>
              </AboutContent>
            </AboutHeader>
            
            <StyledPhilosophyContainer
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Anchor id="philosophy" />
              <SectionHeading
                themeMode="light"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                {getText('philosophyTitle')}
              </SectionHeading>
              
              <motion.div
                variants={staggerContainer}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
              >
                <AboutText variants={fadeIn}>
                  {getText('philosophy1')}
                </AboutText>
                
                <AboutText variants={fadeIn}>
                  {getText('philosophy2')}
                </AboutText>
                
                <AboutText variants={fadeIn}>
                  {getText('philosophy3')}
                </AboutText>
              </motion.div>
            </StyledPhilosophyContainer>
          </AboutContainer>
        </AboutSection>
        
        <ValuesSection>
          <ValuesContainer>
            <Anchor id="coreValues" />
            <SectionHeading
              themeMode="dark"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              {getText('valuesTitle')}
            </SectionHeading>
            
            <ValuesGrid>
              <ValueCard
                whileHover={{ y: -10 }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <ValueTitle>{getText('innovationTitle')}</ValueTitle>
                <ValueText>
                  {getText('innovationText')}
                </ValueText>
              </ValueCard>
              
              <ValueCard
                whileHover={{ y: -10 }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <ValueTitle>{getText('globalImpactTitle')}</ValueTitle>
                <ValueText>
                  {getText('globalImpactText')}
                </ValueText>
              </ValueCard>
              
              <ValueCard
                whileHover={{ y: -10 }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <ValueTitle>{getText('purposeDrivenTitle')}</ValueTitle>
                <ValueText>
                  {getText('purposeDrivenText')}
                </ValueText>
              </ValueCard>
              
              <ValueCard
                whileHover={{ y: -10 }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <ValueTitle>{getText('adaptabilityTitle')}</ValueTitle>
                <ValueText>
                  {getText('adaptabilityText')}
                </ValueText>
              </ValueCard>
              
              <ValueCard
                whileHover={{ y: -10 }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <ValueTitle>{getText('empowermentTitle')}</ValueTitle>
                <ValueText>
                  {getText('empowermentText')}
                </ValueText>
              </ValueCard>
              
              <ValueCard
                whileHover={{ y: -10 }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                viewport={{ once: true }}
              >
                <ValueTitle>{getText('executionTitle')}</ValueTitle>
                <ValueText>
                  {getText('executionText')}
                </ValueText>
              </ValueCard>
            </ValuesGrid>
          </ValuesContainer>
        </ValuesSection>
        
        <JourneySection>
          <JourneyContainer>
            <Anchor id="journey" />
            <SectionHeading
              themeMode="light"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              {getText('journeyTitle')}
            </SectionHeading>
            
            <JourneySubtitle
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              {getText('journeySubtitle')}
            </JourneySubtitle>
            
            <Timeline>
              <TimelineItem
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <TimelineDot />
                <TimelineContent>
                  <TimelineDate>{getText('journey2023')}</TimelineDate>
                  <TimelineTitle>{getText('journey2023Title')}</TimelineTitle>
                  <TimelineText>
                    {getText('journey2023Text')}
                  </TimelineText>
                </TimelineContent>
              </TimelineItem>
              
              <TimelineItem
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <TimelineDot />
                <TimelineContent>
                  <TimelineDate>{getText('journey2020')}</TimelineDate>
                  <TimelineTitle>{getText('journey2020Title')}</TimelineTitle>
                  <TimelineText>
                    {getText('journey2020Text')}
                  </TimelineText>
                </TimelineContent>
              </TimelineItem>
              
              <TimelineItem
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <TimelineDot />
                <TimelineContent>
                  <TimelineDate>{getText('journey2018')}</TimelineDate>
                  <TimelineTitle>{getText('journey2018Title')}</TimelineTitle>
                  <TimelineText>
                    {getText('journey2018Text')}
                  </TimelineText>
                </TimelineContent>
              </TimelineItem>
              
              <TimelineItem
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <TimelineDot />
                <TimelineContent>
                  <TimelineDate>{getText('journey2015')}</TimelineDate>
                  <TimelineTitle>{getText('journey2015Title')}</TimelineTitle>
                  <TimelineText>
                    {getText('journey2015Text')}
                  </TimelineText>
                </TimelineContent>
              </TimelineItem>
              
              <TimelineItem
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <TimelineDot />
                <TimelineContent>
                  <TimelineDate>{getText('journey2009')}</TimelineDate>
                  <TimelineTitle>{getText('journey2009Title')}</TimelineTitle>
                  <TimelineText>
                    {getText('journey2009Text')}
                  </TimelineText>
                </TimelineContent>
              </TimelineItem>
            </Timeline>
            
            <JourneySubtitle
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              viewport={{ once: true }}
              style={{ marginTop: '40px', fontStyle: 'italic' }}
            >
              Today, I still start from scratch every day, because the world is still waiting to be changed.
            </JourneySubtitle>
          </JourneyContainer>
        </JourneySection>
      </motion.div>
    </motion.div>
  );
};

export default About; 